import { memo, useEffect, useRef, useState } from "react";

import DeviceMap from "./device-map/DeviceMap";
import Table, { ColumnType } from "antd/es/table";
import DeviceStatus from "../device-status/DeviceStatus";
import StringUtils from "../../../util/StringUtils";
import DeviceApi from "../../../api/DeviceApi";
import { DeviceSelectionContent, DeviceSelectionHeader, DeviceSelectionLeft, DeviceSelectionRight, DeviceSelectionTableWrapper, DeviceSelectionWrapper, OperationWrapper, SearchInputWrapper, ToolBarWrapper } from "./DeviceSelection.styled";
import { Button, Input, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";

interface DeviceSelectionProps {
    multiSelect?: boolean,
    filter?: (deviceView: any)=> boolean;
    fetchDeviceViews?: ()=> Promise<any[]>;
    onOk: (selectedDeviceViews: any[])=> void;
    onCancel: ()=> void;
}

const DeviceSelection: React.FC<DeviceSelectionProps> = memo((props)=> {
    // api
    let deviceApi = new DeviceApi();

    // state
    let [deviceViews, setDeviceViews] = useState([]);
    let [selectedDeviceViews, setSelectedDeviceViews] = useState([]);
    let [keyword, setKeyword] = useState("");
    let [loading, setLoading] = useState(true);

    let filterDeviceViews = StringUtils.isEmpty(keyword) ? deviceViews : 
        deviceViews.filter(e => e.device.name.toLowerCase().indexOf(keyword.toLowerCase()) > -1);

    // load device views
    let loadDeviceViews = async ()=> {
        // loading animation
        setLoading(true);

        // do load device views
        let deviceViews:any[] = [];
        if (props.fetchDeviceViews) {
            deviceViews = await props.fetchDeviceViews();
        } else {
            deviceViews = (await deviceApi.getAllDeviceViews()).data.data;
        }

        // filter device views
        if (props.filter) {
            deviceViews = deviceViews.filter(props.filter);
        }

        // set device views and close animation
        setDeviceViews(deviceViews);
        setLoading(false);
    };

    let onSearch = (value)=> {
        setKeyword(value);
    };

    let onDeviceSelected = (selectedDeviceView: any, selected: boolean)=> {
        if (props.multiSelect) {
            if (selected) {
                let newSelectedDeviceViews = [...selectedDeviceViews, selectedDeviceView];
                setSelectedDeviceViews(newSelectedDeviceViews);
            } else {
                let newSelectedDeviceViews = selectedDeviceViews.filter(e => e.device.id !== selectedDeviceView.device.id);
                setSelectedDeviceViews(newSelectedDeviceViews);
            }
        } else {
            setSelectedDeviceViews([selectedDeviceView]);
            onOk();
        }
    };

    let onOk = ()=> {
        props.onOk(selectedDeviceViews);
    };

    let onCancel = ()=> {
        props.onCancel();
    }

    // use effect
    useEffect(()=> {
        loadDeviceViews();
    }, []);

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '状态',
            dataIndex: 'device.status',
            key: "device.status",
            align: 'center',
            render: (text, record, index)=> {
                return <DeviceStatus status={record.device.status} />;
            }
        },
        {
            title: '设备名称',
            dataIndex: 'device.name',
            key: 'device.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.device.name}</span>
            }
        },
        {
            title: '序列号',
            dataIndex: 'node.sn',
            key: 'node.sn',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.node?.sn}</span>
            }
        },
        {
            title: '设备模板',
            dataIndex: 'template.name',
            key: 'template.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.deviceTemplate?.name}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'device.remarks',
            key: 'device.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.device.remarks)}</span>
            }
        }
    ];

    // render
    return <DeviceSelectionWrapper>
        <DeviceSelectionHeader>选择设备</DeviceSelectionHeader>
        <DeviceSelectionContent>
            <DeviceSelectionLeft>
                <ToolBarWrapper>
                    <SearchInputWrapper>
                        <Input 
                            size="large"
                            placeholder="请输入设备名称或序列号" 
                            suffix={<SearchOutlined />}
                            onChange={(e)=> onSearch(e.target.value)}  
                            />
                    </SearchInputWrapper>
                </ToolBarWrapper>

                <DeviceSelectionTableWrapper>
                    <Table
                        rowKey={(record)=> record.device.id}
                        columns={columns} 
                        dataSource={filterDeviceViews}
                        loading={loading}
                        pagination={false}
                        rowSelection={{
                            type: props.multiSelect ? 'checkbox' : 'radio', 
                            onSelect: onDeviceSelected,
                            selectedRowKeys: selectedDeviceViews.map(e => e.device.id),
                        }}
                    />
                </DeviceSelectionTableWrapper>
            </DeviceSelectionLeft>

            <DeviceSelectionRight>
                <DeviceMap 
                    deviceViews={deviceViews} 
                    selectedDeviceId={selectedDeviceViews[selectedDeviceViews.length-1]?.device?.id} 
                    onDeviceSelected={(selectedDeviceId)=> {
                        let deviceView = deviceViews.find(deviceView => deviceView.device.id === selectedDeviceId)
                        onDeviceSelected(deviceView, true);
                    }}>
                </DeviceMap>
            </DeviceSelectionRight>
        </DeviceSelectionContent>

        <OperationWrapper>
            <Space size="middle">
                <Button type="primary" disabled={selectedDeviceViews==null} onClick={onOk}>确定</Button>
                <Button type="primary" danger onClick={onCancel}>取消</Button>
            </Space>
        </OperationWrapper>
    </DeviceSelectionWrapper>
});

export default DeviceSelection;